<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-4">
        <h2 class="font-weight-bolder">{{ isNaccuUser ? $t('memberDataSection.buyingStationOrder.bulkImport.titleCherry') : $t('memberDataSection.buyingStationOrder.bulkImport.title') }}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined class="mr-2" @click="goBack">{{ $t('back') }}</v-btn>
      </div>

      <v-card v-if="detail" elevation="0" class="px-5">
        <v-card-text class="text-body-1 black--text pa-0">
          <div class="px-4 py-4">
            <!-- Progress -->
            <div class="bso-progress-section">
              <span class="bso-progress-title text--secondary">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.importCompleted') }}
              </span>
              <div class="bso-progress-row mt-2">
                <v-progress-linear rounded height="12" :value="progressPercent" color="kenyaGreen" background-color="green2" class="flex-grow-1"></v-progress-linear>
                <span class="bso-progress-percent kenyaGreen--text">{{ progressPercent }}%</span>
              </div>
              <p class="bso-progress-status text--secondary mt-2 mb-0">
                <span class="font-weight-bold">{{ importedRecordsCount }}</span>
                {{ $t('of') }}
                <span class="font-weight-bold">{{ totalRecordsCount }}</span>
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.recordsReadyToImport') }}
              </p>
            </div>

            <!-- Summary cards -->
            <v-row class="mt-4" dense>
              <v-col v-for="card in summaryCards" :key="card.key" cols="12" sm="4">
                <div class="bso-summary-card" :style="{ borderLeftColor: themeColor(card.color) }">
                  <div class="bso-summary-card__value" :style="{ color: themeColor(card.color) }">{{ card.count }}</div>
                  <div class="bso-summary-card__label text--secondary">{{ card.label }}</div>
                </div>
              </v-col>
            </v-row>

            <!-- Filter chips -->
            <div class="bso-chips mt-5 d-flex flex-wrap">
              <button
                v-for="chip in filterChips"
                :key="chip.value"
                type="button"
                class="bso-chip"
                :style="chipStyle(chip)"
                @click="resultFilter = chip.value"
              >
                <v-icon v-if="chip.icon" small :color="chipIconColor(chip)" class="mr-1">{{ chip.icon }}</v-icon>
                {{ chip.label }} ({{ chip.count }})
              </button>
            </div>

            <!-- Issues alert -->
            <div
              v-if="issuesRecordsCount > 0"
              class="bso-issues-alert mt-4"
              :style="{ backgroundColor: themeColor('secondary2light'), borderColor: themeColor('third') }"
            >
              <p class="font-weight-bold black--text mb-2">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.issuesDetectedHeading') }}
              </p>
              <p class="text-body-2 mb-3" style="color:#424242;">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.issuesDetectedDescription') }}
              </p>
              <v-checkbox
                v-model="deleteDiscrepancyLots"
                hide-details
                class="mt-0 pt-0"
                color="third"
                :disabled="isLocked || actionLoading"
                @change="onDeleteAllIssues"
              >
                <template v-slot:label>
                  <span class="text-body-2 black--text">
                    {{ $t('memberDataSection.buyingStationOrder.bulkImport.deleteRowsWithIssues') }}
                  </span>
                </template>
              </v-checkbox>
            </div>

            <!-- Records table -->
            <v-card class="mt-4 bso-table-card" elevation="1">
              <v-data-table
                item-key="id"
                disable-pagination
                hide-default-footer
                :headers="tableHeaders"
                :items="filteredRecords"
                class="bso-table"
                @click:row="openDetail"
              >
                <template v-slot:item.purchaseDate="{ item }">{{ formatDate(rec(item).purchaseDate) }}</template>
                <template v-slot:item.farmer="{ item }">
                  <span :class="{ 'red--text': hasError(item, 'farmerId') || hasError(item, 'farmerName') }">
                    {{ rec(item).farmerName || '—' }}
                  </span>
                  <v-tooltip v-if="item.isError" bottom max-width="360">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" small color="third" class="ml-1">mdi-alert</v-icon>
                    </template>
                    <div class="bso-tooltip">{{ issueText(item) }}</div>
                  </v-tooltip>
                </template>
                <template v-slot:item.farm="{ item }">
                  <span :class="{ 'red--text': hasError(item, 'farmId') }">{{ rec(item).farmName || '—' }}</span>
                </template>
                <template v-slot:item.buyingStation="{ item }">{{ rec(item).buyingStationName || '—' }}</template>
                <template v-slot:item.coffee="{ item }">
                  <div :class="{ 'red--text': hasError(item, 'coffeeCherryQty') || hasError(item, 'coffeeCherryQlty') }">
                    <div>{{ rec(item).coffeeCherryQty != null ? rec(item).coffeeCherryQty + ' kg' : '—' }}</div>
                    <div class="text-caption">{{ rec(item).coffeeCherryQlty || '—' }}</div>
                  </div>
                </template>
                <template v-slot:item.status="{ item }">
                  <v-btn small rounded depressed min-width="140" height="34" class="bso-status-btn non-clickable" :style="statusPillStyle(item)">
                    {{ statusLabel(item) }}
                  </v-btn>
                </template>
                <template v-slot:item.action="{ item }">
                  <div class="d-flex align-center justify-center flex-nowrap" style="gap:4px;">
                    <v-btn icon small elevation="2" class="mr-1" :disabled="isLocked || actionLoading" @click.stop="editRecord(item)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon small elevation="2" :disabled="isLocked || actionLoading" @click.stop="deleteRecord(item)">
                      <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card>

            <v-divider class="my-5 px-0"></v-divider>
            <v-card-actions class="px-0">
              <v-spacer></v-spacer>
              <v-btn outlined color="primary" class="mr-2" width="120" :disabled="isLocked || actionLoading || importLoading" @click="cancelImportAction">
                {{ $t('cancel') }}
              </v-btn>
              <v-btn color="primary" class="white--text" :disabled="!canImport" :loading="importLoading" @click="runImportAction">
                {{ $t('oma.import') }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-else-if="!loading" elevation="0" class="px-5 pa-8 text-center text--secondary">
        {{ $t('memberDataSection.buyingStationOrder.bulkImport.noDataFound') }}
        <div class="mt-4"><v-btn color="primary" outlined @click="goBack">{{ $t('back') }}</v-btn></div>
      </v-card>

      <BulkImportDetailDialog v-model="detailDialog" :record="selectedRecord" @edit="onDetailEdit" />
      <BulkImportEditDialog v-model="editDialog" :record="editingRecord" :loading="actionLoading" @save="onEditSave" />
      <BulkImportSuccessDialog v-model="successDialog" :count="importedCount" @close="onSuccessClose" />

      <ConfirmBox
        :dialog="confirm.dialog"
        :title="confirm.title"
        :message="confirm.message"
        :confirmBtnText="confirm.confirmBtnText"
        :cancelBtnText="$t('cancel')"
        @cancel:action="onConfirmCancel"
        @continue:action="onConfirmContinue"
      />

      <v-overlay :value="actionLoading || importLoading || loading" z-index="300">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import {
  isSuccessRecord,
  isErrorRecord,
  mapServerRecord,
  mapServerImport,
  SERVER_ERROR_CODE_I18N,
  SERVER_ERROR_CODE_LABEL_I18N,
  SERVER_BULK_IMPORT_STATUS,
} from "@/utils/buyingStationOrderBulkImport";
import BuyingStationOrderBulkImportService from "@/_services/BuyingStationOrderBulkImportService";
import BulkImportEditDialog from "./BulkImportEditDialog.vue";
import BulkImportDetailDialog from "./BulkImportDetailDialog.vue";
import BulkImportSuccessDialog from "./BulkImportSuccessDialog.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import { isKenyaClient } from "@/utils";

const B = "memberDataSection.buyingStationOrder.bulkImport";
const RESULT_FILTER = { ALL: "all", SUCCESS: "success" };

export default {
  name: "BuyingStationOrderBulkImportResult",
  components: { BulkImportEditDialog, BulkImportDetailDialog, BulkImportSuccessDialog, ConfirmBox },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      detail: null,
      bulkImportId: null,
      resultFilter: RESULT_FILTER.ALL,
      deleteDiscrepancyLots: false,
      loading: false,
      actionLoading: false,
      importLoading: false,
      detailDialog: false,
      selectedRecord: null,
      editDialog: false,
      editingRecord: null,
      successDialog: false,
      importedCount: 0,
      confirm: { dialog: false, title: "", message: "", confirmBtnText: "", handler: null },
      tableHeaders: [
        { text: this.$t(`${B}.purchaseDate`), value: "purchaseDate", class: "bso-th" },
        { text: this.$t(`${B}.farmer`), value: "farmer", class: "bso-th" },
        { text: this.$t(`${B}.farm`), value: "farm", class: "bso-th" },
        {
          text: isKenyaClient() ? this.$t("memberDataSection.factory") : this.$t(`${B}.buyingStation`),
          value: "buyingStation",
          class: "bso-th",
        },
        { text: this.$t(`${B}.coffeeQuantityQuality`), value: "coffee", class: "bso-th" },
        { text: this.$t("oma.status"), value: "status", align: "center", class: "bso-th" },
        { text: this.$t("oma.action"), value: "action", align: "center", sortable: false, class: "bso-th", width: "120px" },
      ],
    };
  },
  computed: {
    isNaccuUser() {
      return isKenyaClient();
    },
    isLocked() {
      return (
        this.detail?.status === SERVER_BULK_IMPORT_STATUS.CANCELLED ||
        this.detail?.status === SERVER_BULK_IMPORT_STATUS.SUCCESS
      );
    },
    canImport() {
      return (
        !this.isLocked &&
        !this.actionLoading &&
        !this.importLoading &&
        Boolean(this.bulkImportId) &&
        this.successRecordsCount > 0 &&
        this.issuesRecordsCount === 0
      );
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        {
          text: this.isNaccuUser ? this.$t("memberDataSection.factories") : this.$t("memberDataSection.buyingStation"),
          disabled: false,
          to: { name: "CoffeeBuyingStation" },
          exact: true,
        },
        {
          text: this.isNaccuUser ? this.$t(`${B}.titleCherry`) : this.$t(`${B}.title`),
          disabled: true,
          href: "#",
        },
      ];
    },
    allRecords() {
      return this.detail?.records || [];
    },
    successRecords() {
      return this.allRecords.filter((r) => isSuccessRecord(r));
    },
    issueRecords() {
      return this.allRecords.filter((r) => isErrorRecord(r));
    },
    totalRecordsCount() {
      return this.detail?.totalRecordsCount || 0;
    },
    successRecordsCount() {
      return this.detail?.successRecordsCount ?? this.successRecords.length;
    },
    issuesRecordsCount() {
      return this.detail?.issuesRecordsCount ?? this.issueRecords.length;
    },
    importedRecordsCount() {
      return this.successRecordsCount;
    },
    progressPercent() {
      const total = this.totalRecordsCount;
      if (!total) return 0;
      return Math.min(100, Math.round((this.importedRecordsCount / total) * 100));
    },
    errorCodeCounts() {
      const counts = {};
      this.issueRecords.forEach((r) => {
        (r.errorCodes || []).forEach((c) => {
          counts[c] = (counts[c] || 0) + 1;
        });
      });
      return counts;
    },
    summaryCards() {
      return [
        { key: "total", color: "gray", label: this.$t(`${B}.tabTotalRecords`), count: this.totalRecordsCount },
        { key: "success", color: "kenyaGreen", label: this.$t(`${B}.tabSuccess`), count: this.successRecordsCount },
        { key: "issues", color: "third", label: this.$t(`${B}.tabIssues`), count: this.issuesRecordsCount },
      ];
    },
    filterChips() {
      const chips = [
        { value: RESULT_FILTER.ALL, label: this.$t(`${B}.filterAll`), count: this.totalRecordsCount, icon: "mdi-check", color: "kenyaGreen" },
        { value: RESULT_FILTER.SUCCESS, label: this.$t(`${B}.filterSuccess`), count: this.successRecordsCount, icon: "mdi-check", color: "kenyaGreen" },
      ];
      Object.keys(this.errorCodeCounts).forEach((code) => {
        const key = SERVER_ERROR_CODE_LABEL_I18N[code];
        chips.push({
          value: code,
          label: key ? this.$t(`${B}.${key}`) : code,
          count: this.errorCodeCounts[code],
          icon: "mdi-alert",
          color: "third",
        });
      });
      return chips;
    },
    filteredRecords() {
      if (this.resultFilter === RESULT_FILTER.SUCCESS) return this.successRecords;
      if (this.resultFilter === RESULT_FILTER.ALL) return this.allRecords;
      return this.allRecords.filter((r) => (r.errorCodes || []).includes(this.resultFilter));
    },
  },
  watch: {
    id() {
      this.fetchDetail();
    },
  },
  mounted() {
    this.fetchDetail();
  },
  methods: {
    async fetchDetail() {
      this.bulkImportId = this.id != null ? this.id : null;
      if (!this.bulkImportId) {
        this.detail = null;
        return;
      }
      this.loading = true;
      try {
        const res = await BuyingStationOrderBulkImportService.getImportById(this.bulkImportId);
        if (res && res.success !== false && res.data) {
          this.detail = mapServerImport(res.data);
          this.bulkImportId = this.detail.bulkImportId || this.bulkImportId;
        } else {
          this.detail = null;
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.detail = null;
        this.notifyError(err && err.message);
      } finally {
        this.loading = false;
      }
    },
    refreshCounts() {
      if (!this.detail) return;
      this.detail.totalRecordsCount = this.detail.records.length;
      this.detail.successRecordsCount = this.successRecords.length;
      this.detail.issuesRecordsCount = this.issueRecords.length;
      this.detail.errorRecordsCount = this.issueRecords.length;
      this.detail.importedRecordsCount = this.successRecords.length;
    },
    themeColor(name) {
      const theme = this.$vuetify.theme;
      const variant = theme.dark ? "dark" : "light";
      return (theme.themes[variant] && theme.themes[variant][name]) || "";
    },
    rec(item) {
      return item.recordedJsonData || {};
    },
    hasError(item, field) {
      const md = item.errorMetadata || {};
      return Boolean(md[field] && md[field].length);
    },
    issueText(item) {
      return (item.errorCodes || [])
        .map((c) => {
          const key = SERVER_ERROR_CODE_I18N[c];
          return key ? this.$t(`${B}.${key}`) : "";
        })
        .filter(Boolean)
        .join(" ");
    },
    formatDate(v) {
      return v ? moment(v).format("DD-MMM-YYYY") : "—";
    },
    statusLabel(item) {
      return item.isError ? this.$t(`${B}.issuesDetected`) : this.$t("success");
    },
    statusPillStyle(item) {
      const mainName = item.isError ? "third" : "kenyaGreen";
      const bgName = item.isError ? "secondary2light" : "green2";
      const main = this.themeColor(mainName);
      return { color: main, backgroundColor: this.themeColor(bgName), border: `2px solid ${main}` };
    },
    chipStyle(chip) {
      const active = this.resultFilter === chip.value;
      if (active) return { backgroundColor: this.themeColor(chip.color), color: this.themeColor("white") };
      return { backgroundColor: this.themeColor("themeGrey"), color: this.themeColor("lightBlack") };
    },
    chipIconColor(chip) {
      return this.resultFilter === chip.value ? "white" : chip.color;
    },

    openDetail(item) {
      this.selectedRecord = item;
      this.detailDialog = true;
    },
    onDetailEdit(record) {
      this.detailDialog = false;
      this.editRecord(record);
    },
    editRecord(item) {
      this.editingRecord = item;
      this.editDialog = true;
    },
    async onEditSave({ recordId, payload }) {
      this.actionLoading = true;
      try {
        const res = await BuyingStationOrderBulkImportService.updateRecord(recordId, payload);
        if (res && res.success !== false && res.data) {
          this.replaceRecord(mapServerRecord(res.data));
          this.refreshCounts();
          this.editDialog = false;
          this.editingRecord = null;
          this.$notify({ type: "success", text: res.message || this.$t(`${B}.recordUpdatedSuccess`) });
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },
    replaceRecord(mapped) {
      if (!this.detail || !this.detail.records) return;
      const idx = this.detail.records.findIndex((r) => r.id === mapped.id);
      if (idx >= 0) this.$set(this.detail.records, idx, mapped);
    },
    deleteRecord(item) {
      this.openConfirm({
        title: this.$t(`${B}.deleteRecord`),
        message: this.$t(`${B}.deleteRecordConfirm`),
        confirmBtnText: this.$t("delete"),
        handler: () => this.performDelete([item.id]),
      });
    },
    onDeleteAllIssues(checked) {
      this.deleteDiscrepancyLots = false;
      if (!checked) return;
      const ids = this.issueRecords.map((r) => r.id);
      if (!ids.length) return;
      this.openConfirm({
        title: this.$t(`${B}.deleteRowsWithIssues`),
        message: this.$t(`${B}.deleteDiscrepancyConfirm`, { count: ids.length }),
        confirmBtnText: this.$t("delete"),
        handler: () => this.performDelete(ids),
      });
    },
    async performDelete(ids) {
      if (!this.bulkImportId || !ids.length) return;
      this.actionLoading = true;
      try {
        const res = await BuyingStationOrderBulkImportService.deleteRecords(this.bulkImportId, ids);
        if (res && res.success !== false) {
          const idSet = new Set(ids);
          this.detail.records = this.detail.records.filter((r) => !idSet.has(r.id));
          this.refreshCounts();
          if (this.resultFilter !== RESULT_FILTER.ALL && this.resultFilter !== RESULT_FILTER.SUCCESS && !this.errorCodeCounts[this.resultFilter]) {
            this.resultFilter = RESULT_FILTER.ALL;
          }
          this.$notify({ type: "success", text: res.message || this.$t(`${B}.recordsDeletedSuccess`) });
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },
    cancelImportAction() {
      if (!this.bulkImportId) {
        this.goBack();
        return;
      }
      this.openConfirm({
        title: this.$t(`${B}.cancelImportTitle`),
        message: this.$t(`${B}.cancelImportConfirm`),
        confirmBtnText: this.$t(`${B}.cancelImportConfirmBtn`),
        handler: () => this.performCancel(),
      });
    },
    async performCancel() {
      this.actionLoading = true;
      try {
        const res = await BuyingStationOrderBulkImportService.cancelImport(this.bulkImportId);
        if (res && res.success !== false) {
          if (this.detail) this.detail.status = SERVER_BULK_IMPORT_STATUS.CANCELLED;
          this.$notify({ type: "success", text: res.message || this.$t(`${B}.importCancelledSuccess`) });
          this.goBack();
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.actionLoading = false;
      }
    },
    runImportAction() {
      if (!this.canImport) return;
      this.openConfirm({
        title: this.$t("oma.import"),
        message: this.$t(`${B}.importConfirm`, { count: this.successRecordsCount }),
        confirmBtnText: this.$t("oma.import"),
        handler: () => this.performImport(),
      });
    },
    async performImport() {
      this.importLoading = true;
      try {
        const res = await BuyingStationOrderBulkImportService.runImport(this.bulkImportId);
        if (res && res.success !== false) {
          if (this.detail) this.detail.status = SERVER_BULK_IMPORT_STATUS.SUCCESS;
          this.importedCount = res.data && res.data.importedCount != null ? res.data.importedCount : this.successRecordsCount;
          this.successDialog = true;
        } else {
          this.notifyError(res && res.message);
        }
      } catch (err) {
        this.notifyError(err && err.message);
      } finally {
        this.importLoading = false;
      }
    },
    onSuccessClose() {
      this.successDialog = false;
      this.$router.push({ name: "CoffeeBuyingStation" });
    },
    openConfirm({ title, message, confirmBtnText, handler }) {
      this.confirm = { dialog: true, title: title || "", message: message || "", confirmBtnText: confirmBtnText || this.$t("confirm"), handler: handler || null };
    },
    onConfirmCancel() {
      this.confirm.dialog = false;
      this.confirm.handler = null;
    },
    onConfirmContinue() {
      const handler = this.confirm.handler;
      this.confirm.dialog = false;
      this.confirm.handler = null;
      if (typeof handler === "function") handler();
    },
    notifyError(message) {
      this.$notify({ type: "error", text: message || this.$t(`${B}.genericError`) });
    },
    goBack() {
      this.$router.push({ name: "CoffeeBuyingStation" });
    },
  },
};
</script>

<style lang="scss" scoped>
.bso-progress-title { font-size: 14px; }
.bso-progress-row { display: flex; align-items: center; gap: 12px; }
.bso-progress-percent { font-weight: 700; font-size: 14px; min-width: 40px; text-align: right; }
.bso-progress-status { font-size: 14px; }
.bso-summary-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px 20px 20px 24px;
  border-left: 6px solid transparent;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.bso-summary-card__value { font-size: 36px; font-weight: 700; line-height: 1.1; }
.bso-summary-card__label { font-size: 14px; margin-top: 4px; }
.bso-chips { gap: 12px; }
.bso-chip {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.bso-issues-alert { border: 1px solid; border-radius: 8px; padding: 20px 24px; }
.bso-table-card { max-height: 560px; overflow: auto; }
.bso-table ::v-deep tbody tr { cursor: pointer; }
.bso-table ::v-deep thead th span { text-transform: uppercase; font-weight: 600; }
.bso-status-btn { text-transform: none; letter-spacing: 0; font-weight: 500; }
.non-clickable { pointer-events: none; cursor: default; }
.bso-tooltip { max-width: 340px; line-height: 1.45; font-size: 13px; }
</style>
